/**
 * Service layer — Construction · RFIs (Supabase-backed)
 *
 * Wraps /api/rfis (Clerk-authed, service_role). First CA sub-module; RFIs are scoped
 * to a construction_contract. Components call only these functions.
 */

async function call(method, body, qs) {
  const token = await window.__clerkGetToken()
  const url = '/api/rfis' + (qs ? '?' + qs : '')
  const res = await fetch(url, {
    method,
    headers: method === 'GET'
      ? { 'Authorization': 'Bearer ' + token }
      : { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: method === 'GET' ? undefined : JSON.stringify(body),
  })
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    throw new Error(b.error || 'RFI request failed: ' + res.status)
  }
  return res.json()
}

export const RFI_STATUS_LABELS = { open: 'Open', closed: 'Closed', withdrawn: 'Withdrawn', 'Draft Response': 'Draft Response' }

export const RFI_REVIEWING_PARTIES = [
  'Architect', 'Civil Engineer', 'Electrical Engineer', 'Interior Designer',
  'Landscape Architect', 'Structural Engineer', 'Mechanical Engineer', 'Other',
]
export const RFI_ORIGINS = [
  'Unforseeable condition', 'Information on/not on plans', 'Contractor request', 'Other',
]

/** Days between received-from-GC and returned-to-GC (null if not both set). */
export function rfiTurnaround(rfi) {
  if (!rfi?.date_received || !rfi?.date_returned) return null
  const a = new Date(rfi.date_received), b = new Date(rfi.date_returned)
  if (isNaN(a) || isNaN(b)) return null
  return Math.round((b - a) / 86400000)
}

/* ── Reads ────────────────────────────────────────────────────────────────── */

export function getContractRfis(contractId) {
  if (!contractId) return Promise.resolve([])
  return call('GET', null, 'contractId=' + encodeURIComponent(contractId)).then(d => d.rfis || [])
}
export function getRfi(rfiId) {
  return call('GET', null, 'rfiId=' + encodeURIComponent(rfiId)).then(d => d.rfi)
}
/** Reverse lookup: the RFI linked to a Cortex thread (thread → CA mount, legacy items only). */
export function getRfiByThread(threadAirtableId) {
  if (!threadAirtableId) return Promise.resolve(null)
  return call('GET', null, 'threadId=' + encodeURIComponent(threadAirtableId)).then(d => d.rfi || null)
}
/** Reverse lookup: the RFI linked to a milestone (milestone → CA mount). */
export function getRfiByMilestone(milestoneAirtableId) {
  if (!milestoneAirtableId) return Promise.resolve(null)
  return call('GET', null, 'milestoneId=' + encodeURIComponent(milestoneAirtableId)).then(d => d.rfi || null)
}

/* ── Writes ───────────────────────────────────────────────────────────────── */

/** Next RFI number in sequence for the project (fallback contract). */
export function getNextRfiNumber(projectId, contractId) {
  return call('POST', { action: 'nextRfiNumber', projectId, contractId }).then(d => d.next)
}
export function createRfi(contractId, projectId, fields) {
  return call('POST', { action: 'createRfi', contractId, projectId, fields }).then(d => d.rfi)
}
export function updateRfi(rfiId, fields) {
  return call('POST', { action: 'updateRfi', rfiId, fields }).then(d => d.ok)
}
/** Clear the NEW highlight on a portal-submitted RFI once a team member views it. */
export function markRfiSeen(rfiId) {
  return call('POST', { action: 'updateRfi', rfiId, fields: { team_seen_at: new Date().toISOString() } }).then(d => d.ok)
}
export function deleteRfi(rfiId) {
  return call('POST', { action: 'deleteRfi', rfiId }).then(d => d.ok)
}

/* ── Attachments ──────────────────────────────────────────────────────────── */

async function callAttachments(body) {
  const token = await window.__clerkGetToken()
  const res = await fetch('/api/rfi-attachments', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: JSON.stringify(body),
  })
  if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Attachment request failed') }
  return res.json()
}

export function getRfiAttachments(rfiId) {
  return callAttachments({ action: 'list', rfiId }).then(d => d.attachments || [])
}

export function getRfiAttachmentUploadUrl(rfiId, filename) {
  return callAttachments({ action: 'getUploadUrl', rfiId, filename })
}

export function confirmRfiAttachmentUpload(rfiId, storagePath, publicUrl, name, fileType) {
  return callAttachments({ action: 'confirmUpload', rfiId, storagePath, publicUrl, name, fileType })
}

export function deleteRfiAttachment(attachmentId) {
  return callAttachments({ action: 'delete', attachmentId })
}
